import React, { Fragment, useEffect } from 'react';

import { CategoryTree, ContentDeliverable, CourseType, DeliverableType, License, NonAvailableLicense, PendingLicenseRestriction, SelectedContent, SelectedContentReference, TransactionApi } from 'apis/TransactionApi';
import { LicenseRestrictionGMetrixTable } from 'pages/access_codes/components/LicenseRestrictionGMetrixTable/LicenseRestrictionGMetrixTable';
import { GMetrixAccordion, GMetrixAccordionItem } from 'components/GMetrixAccordion/GMetrixAccordion';
import { GMetrixContentTreeview } from 'components/GMetrixContentTreeview/GMetrixContentTreeview';
import { clipText, compareArray, filterOutNonExistentOrderIds, formatTimeString } from 'utils';
import { removeOrdersWithLessUsesThanCurrentTransactionStudents } from 'utils/objects';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { addContentToTransactionChunked } from 'utils/addContentToTransaction';
import { DEFAULT_TRANSACTION_FORM } from 'components/NewAccessCodeForm';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import type { TransactionData } from 'pages/access_codes/context';
import { FormattedMessage, useIntl } from 'react-intl';
import { Col, Container, Row } from 'react-bootstrap';
import { useNotification } from 'app/notifications';
import { TabsContext } from 'components/Tabs/Tabs';
import { GroupData } from 'pages/groups/context';
import { useContext } from 'components/Tabs';
import { TransactionType } from 'types';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'components/FindContent/find-content.scss';

type FindContentPickerProps = {
    ActionsWrapper?: React.ElementType;
    className?: string;
    ContentWrapper?: React.ElementType;
    onClose?: () => void;
    onSuccess?: (content: any) => void;
    selectedContent?: ContentDeliverable[];
    variant: 'picker';
};

type FindContentDialogProps = {
    className?: string;
    currentContext: React.Context<TabsContext<TransactionData>> | React.Context<TabsContext<GroupData>>;
    onCancel: () => void;
    onSuccess?: () => void;
    show: boolean;
    showCancelButton?: boolean;
    variant: 'dialog';
};

export type FindContentProps = FindContentPickerProps | FindContentDialogProps;

const DEFAULT_DELIVERABLE_FILTERS = [
    { Checked: false, DeliverableTypeId: DeliverableType.Test, IsCustomTest: false, Name: 'Practice Tests' },
    { Checked: false, DeliverableTypeId: DeliverableType.Test, IsCustomTest: true, Name: 'Custom Tests' },
    { Checked: false, DeliverableTypeId: DeliverableType.Course, IsCustomTest: false, Name: 'Courses' },
    { Checked: false, DeliverableTypeId: DeliverableType.Book, IsCustomTest: false, Name: 'Books' }
];

type PendingBuckets = {
    orderIds: number[];
    pendingBooks: SelectedContentReference[];
    pendingCourses: SelectedContentReference[];
    pendingTests: SelectedContentReference[];
};

type PendingCounts = {
    bookCount: number;
    courseCount: number;
    xedCourseAdded: number;
};

// Builds the treeview "checked" keys for content that is already selected. The picker also
// matches on ProductId (its selection can span products); the dialog matches Id + type only.
const buildCheckedIds = (
    selectedList: ContentDeliverable[],
    contentFlatList: ContentDeliverable[],
    matchProductId: boolean
): string[] => {

    const idList: string[] = [];
    selectedList.forEach(pieceOfContent => {

        const pieceOfContentExists = contentFlatList.find(
            flatListContent => flatListContent.Id === pieceOfContent.Id &&
                flatListContent.DeliverableTypeId === pieceOfContent.DeliverableTypeId &&
                (!matchProductId || flatListContent.ProductId === pieceOfContent.ProductId)
        );
        if (pieceOfContentExists) {

            idList.push(pieceOfContentExists.key);

        }

    });
    return idList;

};

// Records a piece of content whose license restriction still needs to be resolved by the user.
const addPendingLicenseRestriction = (
    selectedItem: SelectedContent,
    listOfPendingLicenseRestrictions: PendingLicenseRestriction[],
    licenseRestrictions: License[],
    nonAvailableLicenseList: NonAvailableLicense[]
) => {

    const alreadyExists = listOfPendingLicenseRestrictions.some(
        pendingLicenseRestriction => compareArray(pendingLicenseRestriction.OrderIds, selectedItem.OrderIds)
    );

    if (!alreadyExists) {

        const pendingLicenseRestrictionToAdd: PendingLicenseRestriction = {
            ConflictingDeliverableList: [selectedItem],
            LicenseRestrictions: [],
            NonAvailableLicenseList: [...nonAvailableLicenseList],
            OrderIds: selectedItem.OrderIds,
            Valid: true
        };
        selectedItem.OrderIds.forEach(selectedOrderId => {

            const licenseRestriction = licenseRestrictions.find(
                restriction => restriction.OrderId === selectedOrderId
            );
            if (licenseRestriction) {

                pendingLicenseRestrictionToAdd.LicenseRestrictions.push(licenseRestriction);

            }

        });
        listOfPendingLicenseRestrictions.push(pendingLicenseRestrictionToAdd);

    } else {

        const last = listOfPendingLicenseRestrictions[listOfPendingLicenseRestrictions.length - 1];
        last.ConflictingDeliverableList.push(selectedItem);
        last.NonAvailableLicenseList = [...last.NonAvailableLicenseList, ...nonAvailableLicenseList];

    }

};

// Adds the deliverables tied to already-resolved license restrictions into the pending buckets.
// When `content` is supplied (picker), the resolved order id is written back onto the selection.
const insertPendedLicenseRestrictionDeliverables = (
    orderIdsToAdd: number[],
    validPendingLicenseRestrictions: PendingLicenseRestriction[],
    buckets: PendingBuckets,
    content?: ContentDeliverable[]
) => {

    validPendingLicenseRestrictions.forEach((validPendingLicenseRestriction, i) => {

        validPendingLicenseRestriction.ConflictingDeliverableList.forEach(contentLevelObj => {

            switch (true) {

                case contentLevelObj.DeliverableTypeId === DeliverableType.Test:
                    buckets.pendingTests.push({ id: contentLevelObj.Id, restrictedOrderId: orderIdsToAdd[i] });
                    break;
                case contentLevelObj.DeliverableTypeId === DeliverableType.Book:
                    buckets.pendingBooks.push({ id: contentLevelObj.Id, restrictedOrderId: orderIdsToAdd[i] });
                    break;
                case contentLevelObj.DeliverableTypeId === DeliverableType.Course:
                    buckets.pendingCourses.push({ id: contentLevelObj.Id, restrictedOrderId: orderIdsToAdd[i] });
                    break;

            }

            if (content) {

                const contentIndex = content.findIndex(pieceOfContent => pieceOfContent.Id === contentLevelObj.Id);
                if (contentIndex !== -1) {

                    content[contentIndex].RestrictedOrderId = orderIdsToAdd[i];

                }

            }

        });

    });

};

// Classifies one selected deliverable into its pending bucket (or defers it to the license
// restriction resolver when it maps to more than one order). Counts/buckets are mutated in place.
const classifyPendingDeliverable = (
    selectedItem: ContentDeliverable,
    selectedItemReference: SelectedContent,
    buckets: PendingBuckets,
    counts: PendingCounts,
    listOfPendingLicenseRestrictions: PendingLicenseRestriction[],
    handlePendingLicenseRestrictions: (item: SelectedContent, list: PendingLicenseRestriction[]) => void,
    trackRestrictedOrderId: boolean
) => {

    const addSingle = (bucket: SelectedContentReference[]) => {

        bucket.push({ id: selectedItem.Id, restrictedOrderId: selectedItemReference.OrderIds[0] });
        buckets.orderIds.push(selectedItemReference.OrderIds[0]);
        if (trackRestrictedOrderId) {

            selectedItem.RestrictedOrderId = selectedItemReference.OrderIds[0];

        }

    };

    // Prioritize a license order already attached to the transaction: re-using an attached
    // order consumes no additional uses, so resolve to it instead of prompting for a license.
    if (selectedItemReference.OrderIds.length > 1) {

        const alreadyAssignedOrderId = selectedItemReference.OrderIds.find(orderId => buckets.orderIds.includes(orderId));
        if (alreadyAssignedOrderId !== undefined) {

            selectedItemReference.OrderIds = [alreadyAssignedOrderId];

        }

    }

    switch (true) {

        case selectedItem.DeliverableTypeId === DeliverableType.Test && selectedItem.PendingAdd:
            if (selectedItem.PendingAdd && selectedItemReference.OrderIds.length === 1) {

                addSingle(buckets.pendingTests);

            } else if (selectedItem.PendingAdd && selectedItemReference.OrderIds.length > 1) {

                handlePendingLicenseRestrictions(selectedItemReference, listOfPendingLicenseRestrictions);

            }
            break;
        case selectedItem.DeliverableTypeId === DeliverableType.Book && selectedItem.PendingAdd:
            counts.bookCount++;
            if (selectedItem.PendingAdd && selectedItemReference.OrderIds.length === 1) {

                addSingle(buckets.pendingBooks);

            } else if (selectedItem.PendingAdd && selectedItemReference.OrderIds.length > 1) {

                handlePendingLicenseRestrictions(selectedItemReference, listOfPendingLicenseRestrictions);

            }
            break;
        case selectedItem.DeliverableTypeId === DeliverableType.Course && selectedItem.PendingAdd:
            counts.courseCount++;
            if (selectedItem.CourseType === CourseType.XEDCourse && counts.xedCourseAdded !== 1) {

                counts.xedCourseAdded = 1;

            }
            if (selectedItem.PendingAdd && selectedItemReference.OrderIds.length === 1) {

                addSingle(buckets.pendingCourses);

            } else if (selectedItem.PendingAdd && selectedItemReference.OrderIds.length > 1) {

                handlePendingLicenseRestrictions(selectedItemReference, listOfPendingLicenseRestrictions);

            }
            break;

    }

};

/**
 * Shared state + filtering/selection logic for both the standalone content picker and the
 * context-aware content dialog. Everything that does not depend on how the picker is hosted
 * (Modal wrappers vs GMetrixDialog) or how it persists (hand-off vs API save) lives here.
 */
const useContentTree = () => {

    const [state, setState] = useSuperState({
        allContent: [] as CategoryTree[],
        certificateList: [] as ContentDeliverable[],
        check: { ids: [] as string[] },
        deliverableFilterList: DEFAULT_DELIVERABLE_FILTERS.map(filter => ({ ...filter })),
        expand: { ids: [] as string[] },
        expandSelectedContent: false,
        flatListAllContent: [] as ContentDeliverable[],
        licenseRestrictions: [] as License[],
        loadingAllContent: true,
        loadingSaveButton: false,
        pendingLicenseRestrictions: [] as PendingLicenseRestriction[],
        runOnlyOnce: true,
        searchAllTerm: '',
        selected: [] as ContentDeliverable[],
        selectedPendingLicenseRestrictions: [] as License[][],
        showContentOrRestriction: 'Content' as 'Content' | 'Restriction',
        vendorList: [] as ContentDeliverable[]
    });

    const numCheckedLicenses = state.licenseRestrictions.filter(license => license.Checked).length;
    const numCheckedVendors = state.vendorList.filter(vendor => vendor.Checked).length;
    const numCheckedCertificates = state.certificateList.filter(certificate => certificate.CertificateChecked).length;
    const numCheckedDeliverableFilters = state.deliverableFilterList.filter(deliverable => deliverable.Checked).length;

    const getAndSetCertAndVendorLists = (contentFlatList: ContentDeliverable[]) => {

        const vendorList: ContentDeliverable[] = [];
        const certificatesList: ContentDeliverable[] = [];

        for (let i = 0; i < contentFlatList.length; i++) {

            const vendorAlreadyExists = vendorList.find(vendor => {

                if (vendor.VendorId && vendor.VendorId === contentFlatList[i].VendorId) {

                    return vendor;

                }

            });
            const certificateAlreadyExists = certificatesList.find(
                certificate => certificate.CertificationName === contentFlatList[i].CertificationName
            );
            if (contentFlatList[i] && contentFlatList[i].VendorId && !vendorAlreadyExists) {

                contentFlatList[i].Checked = false;
                vendorList.push(contentFlatList[i]);

            }

            if (contentFlatList[i].CertificationName.length > 0 && !certificateAlreadyExists) {

                contentFlatList[i].CertificateChecked = false;
                certificatesList.push(contentFlatList[i]);

            }

        }

        if (vendorList.length > 0) {

            setState({
                vendorList: vendorList.sort((a: ContentDeliverable, b: ContentDeliverable) => {

                    if (a.VendorName < b.VendorName) {

                        return -1;

                    }
                    if (a.VendorName > b.VendorName) {

                        return 1;

                    }
                    return 0;

                })
            });

        }
        if (certificatesList.length > 0) {

            setState({ certificateList: certificatesList });

        }

    };

    const navigateThroughTreeView = (
        filteredTreeView: CategoryTree[],
        checkedItems: any[],
        type: 'vendor' | 'certificate' | 'contentType' | 'license' | 'searchAll'
    ) => {

        const copyOfFilteredTreeView: CategoryTree[] = JSON.parse(JSON.stringify(filteredTreeView));
        const categoryItems = [];
        for (let i = 0; i < copyOfFilteredTreeView.length; i++) {

            const category = copyOfFilteredTreeView[i];
            const productItems = [];
            for (let y = 0; y < category.items.length; y++) {

                const product = category.items[y];
                const deliverableItems = [];
                for (let x = 0; x < product.items.length; x++) {

                    const deliverableType = product.items[x];
                    const newContentItems = [];
                    for (let w = 0; w < deliverableType.items.length; w++) {

                        const contentItem = deliverableType.items[w];
                        switch (type) {

                            case 'vendor': {

                                const matchesAVendor = checkedItems.find(vendor => {

                                    if (vendor.VendorId === contentItem.VendorId) {

                                        return vendor;

                                    }

                                });
                                if (matchesAVendor || contentItem.DeliverableTypeId !== DeliverableType.Course) {

                                    newContentItems.push(contentItem);

                                }
                                break;

                            }
                            case 'certificate': {

                                const matchesACertificate = checkedItems.find(
                                    certificate => certificate.CertificationId === contentItem.CertificationId
                                );
                                if (matchesACertificate) {

                                    newContentItems.push(contentItem);

                                }
                                break;

                            }
                            case 'contentType':
                                for (let index = 0; index < checkedItems.length; index++) {

                                    const contentType = checkedItems[index];
                                    if (contentType.DeliverableTypeId === contentItem.DeliverableTypeId) {

                                        if (contentItem.DeliverableTypeId === DeliverableType.Test) {

                                            if (contentType.IsCustomTest === contentItem.IsCustomTest) {

                                                newContentItems.push(contentItem);

                                            }

                                        } else {

                                            newContentItems.push(contentItem);

                                        }

                                    }

                                }
                                break;
                            case 'license': {

                                let contentHasASelectedLicense = false;
                                contentItem.OrderIds.forEach(orderId => {

                                    if (
                                        checkedItems.find(license => license.OrderId === orderId) &&
                                        !contentHasASelectedLicense
                                    ) {

                                        contentHasASelectedLicense = true;
                                        newContentItems.push(contentItem);

                                    }

                                });
                                break;

                            }
                            case 'searchAll':
                                if (
                                    contentItem.CategoryName.toLowerCase().includes(state.searchAllTerm.toLowerCase()) ||
                                    contentItem.ProductName.toLowerCase().includes(state.searchAllTerm.toLowerCase()) ||
                                    contentItem.Name.toLowerCase().includes(state.searchAllTerm.toLowerCase()) ||
                                    contentItem.CertificationName.toLowerCase().includes(state.searchAllTerm.toLowerCase())
                                ) {

                                    newContentItems.push(contentItem);

                                }
                                break;

                        }

                    }
                    deliverableType.items = newContentItems;
                    if (deliverableType.items.length > 0) {

                        deliverableItems.push(deliverableType);

                    }

                }
                product.items = deliverableItems;
                if (product.items.length > 0) {

                    productItems.push(product);

                }

            }
            category.items = productItems;
            if (category.items.length > 0) {

                categoryItems.push(category);

            }

        }

        return categoryItems;

    };

    const customFilterFunc = () => {

        const checkedLicenses = state.licenseRestrictions.filter(license => license.Checked);
        const checkedVendors = state.vendorList.filter(vendor => vendor.Checked);
        const checkedContentTypes = state.deliverableFilterList.filter(contentType => contentType.Checked);
        const checkedCertificates = state.certificateList.filter(certificate => certificate.CertificateChecked);
        let filteredTreeView: CategoryTree[] = state.allContent;

        if (state.searchAllTerm.length > 0) {

            filteredTreeView = navigateThroughTreeView(filteredTreeView, [], 'searchAll');

        }

        if (checkedVendors.length > 0) {

            filteredTreeView = navigateThroughTreeView(filteredTreeView, checkedVendors, 'vendor');

        }

        if (checkedContentTypes.length > 0) {

            filteredTreeView = navigateThroughTreeView(filteredTreeView, checkedContentTypes, 'contentType');

        }

        if (checkedCertificates.length > 0) {

            filteredTreeView = navigateThroughTreeView(filteredTreeView, checkedCertificates, 'certificate');

        }

        if (checkedLicenses.length > 0) {

            filteredTreeView = navigateThroughTreeView(filteredTreeView, checkedLicenses, 'license');
            filteredTreeView = filteredTreeView.filter((treeItem: CategoryTree) => {

                if (checkedLicenses.length > 0) {

                    for (let i = 0; i < checkedLicenses.length; i++) {

                        const checkedLicense = checkedLicenses[i];
                        const anyOrderIdsMatch = treeItem.OrderIds.find(
                            (orderId: number) => orderId === checkedLicense.OrderId
                        );
                        if (anyOrderIdsMatch) {

                            return treeItem;

                        }

                    }

                }

            });

        }
        return filteredTreeView;

    };

    const onExpandChange = (newExpandedIds: string[]) => {

        setState({ expand: { ids: newExpandedIds } });

    };

    const onCheckChange = (newCheckedIds: string[], newSelected: ContentDeliverable[]) => {

        const mergedSelected: ContentDeliverable[] = [...state.selected].filter(
            selectedContent => selectedContent.PendingAdd === false
        );

        for (let i = 0; i < newSelected.length; i++) {

            const existsInAlreadySelected = mergedSelected.find(
                selectedDeliverable => selectedDeliverable.Id === newSelected[i]?.Id &&
                    selectedDeliverable.DeliverableTypeId === newSelected[i]?.DeliverableTypeId &&
                    selectedDeliverable.ProductId === newSelected[i].ProductId
            );
            if (existsInAlreadySelected === undefined) {

                mergedSelected.push(newSelected[i]);

            }

        }
        setState({ check: { ids: newCheckedIds }, selected: mergedSelected });

    };

    const toggleLicense = (index: number, checked: boolean) => {

        const copyOfLicenses = [...state.licenseRestrictions];
        copyOfLicenses[index].Checked = checked;
        setState({ licenseRestrictions: copyOfLicenses });

    };

    const toggleVendor = (index: number, checked: boolean) => {

        const newVendorList = [...state.vendorList];
        newVendorList[index].Checked = checked;
        setState({ vendorList: newVendorList });

    };

    const toggleCertificate = (index: number, checked: boolean) => {

        const newCertificateList = [...state.certificateList];
        newCertificateList[index].CertificateChecked = checked;
        setState({ certificateList: newCertificateList });

    };

    const toggleDeliverableFilter = (index: number, checked: boolean) => {

        const newDeliverableList = [...state.deliverableFilterList];
        newDeliverableList[index].Checked = checked;
        setState({ deliverableFilterList: newDeliverableList });

    };

    const setSearchAllTerm = (searchAllTerm: string) => setState({ searchAllTerm });

    const setShowContentOrRestriction = (showContentOrRestriction: 'Content' | 'Restriction') => setState({ showContentOrRestriction });

    const setSelectedPendingLicenseRestrictions = (
        value: License[][] | ((current: License[][]) => License[][])
    ) => setState({
        selectedPendingLicenseRestrictions:
            typeof value === 'function' ? value(state.selectedPendingLicenseRestrictions) : value
    });

    return {
        customFilterFunc,
        getAndSetCertAndVendorLists,
        numCheckedCertificates,
        numCheckedDeliverableFilters,
        numCheckedLicenses,
        numCheckedVendors,
        onCheckChange,
        onExpandChange,
        setSearchAllTerm,
        setSelectedPendingLicenseRestrictions,
        setShowContentOrRestriction,
        setState,
        state,
        toggleCertificate,
        toggleDeliverableFilter,
        toggleLicense,
        toggleVendor
    };

};

type ContentTree = ReturnType<typeof useContentTree>;

/**
 * Shared presentational body (sidebar filters + content tree / restriction resolution view).
 * Rendered inside either the picker's Modal wrappers or the dialog's GMetrixDialog shell.
 */
const ContentPickerView = (props: { tree: ContentTree }) => {

    const { tree } = props;
    const { state } = tree;
    const { formatMessage } = useIntl();

    const showSideNav = state.showContentOrRestriction === 'Content' && !state.loadingAllContent;
    const filteredContent = state.showContentOrRestriction === 'Content' ? tree.customFilterFunc() : [];

    const handleLoadIcon = (deliverable: ContentDeliverable, marginSide: 'l' | 'r' = 'r') => {

        let className = '';
        let deliverableTypeText = '';
        switch (true) {

            case deliverable.DeliverableTypeId === DeliverableType.Test:
                if (deliverable.IsCustomTest) {

                    className = 'fa-light fa-file-pen text-warning';
                    deliverableTypeText = formatMessage({ id: 'custom_test.acde46e9', defaultMessage: 'Custom Test' });

                } else {

                    className = 'fa-light fa-ballot-check text-primary';
                    deliverableTypeText = formatMessage({ id: 'test.051b8cd1', defaultMessage: 'Test' });

                }
                break;
            case deliverable.DeliverableTypeId === DeliverableType.Book:
                className = 'fa-light fa-book text-success';
                deliverableTypeText = formatMessage({ id: 'ebook.5d34c24a', defaultMessage: 'EBook' });
                break;
            case deliverable.DeliverableTypeId === DeliverableType.Course:
                className = 'fa-light fa-rectangle-history purple';
                deliverableTypeText = formatMessage({ id: 'course.790646f3', defaultMessage: 'Course' });
                break;
            default:
                break;

        }
        return (
            <i
                className={`m${marginSide}-3 size ${className}`}
                style={{ width: '25px' }}
                title={deliverableTypeText}
            ></i>
        );

    };

    const renderDeliverableInfo = (deliverable: ContentDeliverable) => {

        switch (true) {

            case deliverable.DeliverableTypeId === DeliverableType.Test:
                return (
                    <>
                        {deliverable.TimeInMinutes ? (
                            <div
                                className={`${deliverable.PassingScore ? 'w-50 mx-3' : 'w-100 d-flex justify-content-end'
                                    }`}
                            >
                                <FormattedMessage defaultMessage="Time Limit" id="time_limit.50b63b24" />
                                {' '}
                                -
                                {formatTimeString(deliverable.TimeInMinutes * 60)}
                            </div>
                        ) : null}
                        {deliverable.PassingScore ? (
                            <div
                                className={`${deliverable.TimeInMinutes ? 'w-50 ms-3 me-2' : 'w-100 d-flex justify-content-end'
                                    }`}
                            >
                                <FormattedMessage defaultMessage="Passing Score" id="passing_score.5fe03d6f" />
                                {' '}
                                -
                                {deliverable.PassingScore}
                            </div>
                        ) : null}
                    </>
                );
            case deliverable.DeliverableTypeId === DeliverableType.Book:
                return (
                    <>
                        {deliverable.AuthorName ? (
                            <div
                                className={`${deliverable.VendorName ? 'w-50 mx-3' : 'w-100 d-flex justify-content-end'
                                    }`}
                            >
                                <FormattedMessage defaultMessage="Author" id="author.349be1d2" />
                                {' '}
                                -
                                {deliverable.AuthorName}
                            </div>
                        ) : null}
                        {deliverable.VendorName && (
                            <div
                                className={`${deliverable.AuthorName ? 'w-50 ms-3 me-2' : 'w-100 d-flex justify-content-end'
                                    }`}
                            >
                                <FormattedMessage defaultMessage="Publisher" id="publisher.db0c3c7b" />
                                {' '}
                                -
                                {deliverable.VendorName}
                            </div>
                        )}
                    </>
                );
            case deliverable.DeliverableTypeId === DeliverableType.Course:
                return (
                    <>
                        {deliverable.ContentHours ? (
                            <div
                                className={`${deliverable.VendorName ? 'w-50 mx-3' : 'w-100 d-flex justify-content-end'
                                    }`}
                            >
                                <FormattedMessage defaultMessage="Hours" id="hours.6c9ed7eb" />
                                {' '}
                                -
                                {deliverable.ContentHours}
                            </div>
                        ) : null}
                        {deliverable.VendorName ? (
                            <div
                                className={`${deliverable.ContentHours ? 'w-50 ms-3 me-2' : 'w-100 d-flex justify-content-end'
                                    }`}
                            >
                                <FormattedMessage defaultMessage="Vendor" id="vendor.b7db4ace" />
                                {' '}
                                -
                                {deliverable.VendorName}
                            </div>
                        ) : null}
                    </>
                );
            default:
                return <></>;

        }

    };

    const ItemRender = (item: any) => (
        <div className="find-content-dialog__container__deliverable ms-2 p-3 m-2">
            <div className="w-50 ms-2 me-5" title={item.text}>
                {clipText(item.text, 50)}
            </div>
            <div className="w-50 d-flex justify-content-center align-items-center ms-5">
                {renderDeliverableInfo(item)}
            </div>
        </div>
    );

    const showFilters = () => {

        // this is a janky way to fix the scss issues
        // the turnary was adding an unwanted div which broke the scss
        const filterObject = [];
        if (state.licenseRestrictions.length > 1) {

            filterObject.push(
                <GMetrixAccordionItem
                    className="p-3 my-2"
                    expanded={false}
                    key={'licenseRestrictionKey'}
                    title={`${formatMessage({ id: 'license.2240261c', defaultMessage: 'License', description: 'Formulario' })} ${tree.numCheckedLicenses > 0 ? `(${tree.numCheckedLicenses})` : ''}`}
                >
                    {state.licenseRestrictions.map((restriction, i) => (
                        <div className="find-content-dialog__filter-option" key={i + 'container' + 'restriction'}>
                            <GMetrixCheckInput
                                checked={restriction.Checked}
                                className="find-content-dialog__filter-checkbox"
                                key={i}
                                label={restriction.LicenseName}
                                onChange={e => tree.toggleLicense(i, e.target.checked)}
                                type={'checkbox'}
                            />
                        </div>
                    ))}
                </GMetrixAccordionItem>
            );

        }
        if (state.vendorList.length > 1) {

            filterObject.push(
                <GMetrixAccordionItem
                    title={`${formatMessage({ id: 'vendorpublisher.33c41856', defaultMessage: 'Vendor/Publisher' })} ${tree.numCheckedVendors > 0 ? `(${tree.numCheckedVendors})` : ''
                        }`}
                    className="p-3 my-2"
                    expanded={false}
                    key={'vendorListKey'}
                >
                    {state.vendorList.map((vendor, i) => (
                        <div className="find-content-dialog__filter-option" key={i + 'container' + 'vendor'}>
                            <GMetrixCheckInput
                                checked={vendor.Checked}
                                className="find-content-dialog__filter-checkbox"
                                label={vendor.VendorName}
                                onChange={e => tree.toggleVendor(i, e.target.checked)}
                                type="checkbox"
                            />
                        </div>
                    ))}
                </GMetrixAccordionItem>
            );

        }
        if (state.certificateList.length > 1) {

            filterObject.push(
                <GMetrixAccordionItem
                    title={`${formatMessage({ id: 'certificate.6feeda64', defaultMessage: 'Certificate' })} ${tree.numCheckedCertificates > 0 ? `(${tree.numCheckedCertificates})` : ''
                        }`}
                    className="p-3 my-2"
                    expanded={false}
                    key={'certificateListKey'}
                >
                    {state.certificateList.map((certificate, i) => (
                        <div className="find-content-dialog__filter-option" key={i + 'container' + 'certificate'}>
                            <GMetrixCheckInput
                                checked={certificate.CertificateChecked}
                                className="find-content-dialog__filter-checkbox"
                                key={i}
                                label={certificate.CertificationName}
                                onChange={e => tree.toggleCertificate(i, e.target.checked)}
                                type="checkbox"
                            />
                        </div>
                    ))}
                </GMetrixAccordionItem>
            );

        }
        return filterObject;

    };

    return (
        <div className="find-content-dialog border">
            {showSideNav && (
                <div className="find-content-dialog__side-nav">
                    <div className="p-3 my-2">
                        <GMetrixInput
                            label={formatMessage({ id: 'search_all.1404365c', defaultMessage: 'Search All' })}
                            labelClass="find-content-dialog__text"
                            onChange={e => tree.setSearchAllTerm(e.target.value)}
                            placeholder={formatMessage({ id: 'search_categories_products_or_certificat.80775fce', defaultMessage: 'Search categories, products, or certifications...' })}
                            value={state.searchAllTerm}
                        />
                    </div>
                    <GMetrixAccordion key={'panelBar'}>
                        <GMetrixAccordionItem
                            title={(
                                <b>
                                    <FormattedMessage defaultMessage="Filter" id="filter.a8046688" />
                                </b>
                            )}
                            className="ps-3 my-2"
                        />
                        {showFilters()}
                        <GMetrixAccordionItem
                            title={`${formatMessage({ id: 'content_type.0f2a7eb2', defaultMessage: 'Content Type' })} ${tree.numCheckedDeliverableFilters > 0 ? `(${tree.numCheckedDeliverableFilters})` : ''
                                }`}
                            className="p-3 my-2"
                            expanded={false}
                            key={'filters'}
                        >
                            {state.deliverableFilterList.map((deliverable, i) => (
                                <div
                                    className="find-content-dialog__filter-option"
                                    key={i + 'container' + 'deliverable'}
                                >
                                    <GMetrixCheckInput
                                        checked={deliverable.Checked}
                                        className="find-content-dialog__filter-checkbox"
                                        key={i}
                                        label={deliverable.Name}
                                        onChange={e => tree.toggleDeliverableFilter(i, e.target.checked)}
                                        type="checkbox"
                                    />
                                </div>
                            ))}
                        </GMetrixAccordionItem>
                    </GMetrixAccordion>
                </div>
            )}
            <Container fluid>
                <Row className="find-content-dialog__container">
                    {state.showContentOrRestriction === 'Content' ? (
                        <>
                            {!state.loadingAllContent ? (
                                filteredContent.length > 0 ? (
                                    <GMetrixContentTreeview
                                        checkboxes={true}
                                        checkedIds={state.check.ids}
                                        className="find-content-dialog__tree-view"
                                        data={filteredContent}
                                        expandedIds={state.expand.ids}
                                        flatList={state.flatListAllContent}
                                        onCheckChange={tree.onCheckChange}
                                        onExpandChange={tree.onExpandChange}
                                        renderContent={ItemRender}
                                        renderDeliverableInfo={renderDeliverableInfo}
                                        renderIcon={(item: any) => handleLoadIcon(item, 'r')}
                                    />
                                ) : (
                                    <div className="find-content-dialog__empty-message">
                                        <FormattedMessage defaultMessage="No content is available to select." id="no_content_is_available_to_select.b3a50ff7" />
                                    </div>
                                )
                            ) : (
                                <div className="find-content-dialog__loader">
                                    <GMetrixLoader/>
                                </div>
                            )}
                        </>
                    ) : (
                        <Col>
                            <Row className="d-flex flex-column" style={{ paddingBottom: '50px' }}>
                                <Col className="my-3" lg={12} md={12} sm={12} xl={12}>
                                    <div className="find-content-dialog__restriction-message">
                                        <FormattedMessage defaultMessage={'Please take care of the following issues before we can save your\r\n      content selections'} id="please_take_care_of_the_following_issues.1b622e92" />
                                        .
                                    </div>
                                </Col>
                                {state.pendingLicenseRestrictions.map((licenseRestriction, index) => (
                                    <Col className="my-2" key={index + '-' + 'license-restriction'}>
                                        <LicenseRestrictionGMetrixTable
                                            conflictingDeliverableList={
                                                licenseRestriction.ConflictingDeliverableList
                                            }
                                            nonAvailableLicenseList={
                                                licenseRestriction.NonAvailableLicenseList
                                            }
                                            selectedPendingLicenseRestrictions={
                                                state.selectedPendingLicenseRestrictions
                                            }
                                            setSelectedPendingLicenseRestrictions={
                                                tree.setSelectedPendingLicenseRestrictions
                                            }
                                            key={index}
                                            licenseRestrictions={licenseRestriction.LicenseRestrictions}
                                            parentSelectedPendingLicenseRestrictionIndex={index}
                                            valid={licenseRestriction.Valid}
                                        />
                                    </Col>
                                ))}
                            </Row>
                        </Col>
                    )}
                </Row>
            </Container>
        </div>
    );

};

/**
 * Standalone picker variant: sources selected content from a prop, hands the assembled
 * content payload back through onSuccess, and does NOT persist anything itself.
 */
const PickerVariant = (props: FindContentPickerProps) => {

    const { ActionsWrapper = Fragment, ContentWrapper = Fragment, onClose, onSuccess, selectedContent } = props;

    const tree = useContentTree();
    const { setState, state } = tree;
    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    const setAlreadySelectedCheckBoxes = (contentFlatList: ContentDeliverable[]) => {

        if (selectedContent && selectedContent.length > 0 && contentFlatList.length > 0) {

            setState({
                check: { ...state.check, ids: buildCheckedIds(selectedContent, contentFlatList, true) },
                selected: selectedContent
            });

        }

    };

    useEffect(() => {

        const loadData = async () => {

            if (state.allContent.length > 0) {

                setTimeout(() => setState({ loadingAllContent: false }), 500);

                if (selectedContent) {

                    try {

                        const response = await TransactionApi.GetAllContentForTransactions();
                        setAlreadySelectedCheckBoxes(response.contentFlatList);

                    } catch (error) {

                        console.error(error);
                        setState({ loadingAllContent: false });

                    }

                } else if (state.check.ids && state.check.ids.length === 0) {

                    setAlreadySelectedCheckBoxes(state.flatListAllContent);

                }

            } else {

                try {

                    const [contentResponse, licensesResponse] = await Promise.all([
                        TransactionApi.GetAllContentForTransactions(),
                        TransactionApi.GetAllLicenses()
                    ]);

                    setState({
                        allContent: contentResponse.contentTreeView as CategoryTree[],
                        flatListAllContent: contentResponse.contentFlatList,
                        licenseRestrictions: licensesResponse.licenses,
                        loadingAllContent: false
                    });
                    tree.getAndSetCertAndVendorLists(contentResponse.contentFlatList);

                    if (selectedContent) {

                        setAlreadySelectedCheckBoxes(contentResponse.contentFlatList);

                    }

                } catch (error) {

                    console.error(error);
                    setState({ loadingAllContent: false });

                }

            }

        };

        loadData();

    }, []);

    const handlePendingLicenseRestrictions = (
        selectedItem: SelectedContent,
        listOfPendingLicenseRestrictions: PendingLicenseRestriction[]
    ) => addPendingLicenseRestriction(selectedItem, listOfPendingLicenseRestrictions, state.licenseRestrictions, []);

    const savedOrderIdsAndDeliverableIds = async () => {

        const pendingTests: SelectedContentReference[] = [];
        const pendingBooks: SelectedContentReference[] = [];
        const pendingCourses: SelectedContentReference[] = [];
        let orderIds: number[] = [];
        let listOfPendingLicenseRestrictions: PendingLicenseRestriction[] = [];
        const selectedCheckBoxes = [...state.selected];
        const buckets: PendingBuckets = { orderIds, pendingBooks, pendingCourses, pendingTests };
        const counts: PendingCounts = { bookCount: 0, courseCount: 0, xedCourseAdded: 0 };

        for (let i = 0; i < selectedCheckBoxes.length; i++) {

            const selectedItem = selectedCheckBoxes[i];
            const selectedItemReference: SelectedContent = {
                DeliverableTypeId: selectedItem.DeliverableTypeId,
                Id: selectedItem.Id,
                Name: selectedItem.Name,
                OrderIds: filterOutNonExistentOrderIds(selectedItem.OrderIds, state.licenseRestrictions)
            };

            if (selectedItemReference.OrderIds.length === 0) {

                console.log(`The orderId(s) of this piece of content aren't active or it's empty. ${selectedItem?.OrderIds?.toString()}`, selectedItem);
                break;

            }

            classifyPendingDeliverable(
                selectedItem,
                selectedItemReference,
                buckets,
                counts,
                listOfPendingLicenseRestrictions,
                handlePendingLicenseRestrictions,
                true
            );

        }
        if (listOfPendingLicenseRestrictions.length !== state.pendingLicenseRestrictions.length) {

            setState({
                pendingLicenseRestrictions: listOfPendingLicenseRestrictions,
                selectedPendingLicenseRestrictions: listOfPendingLicenseRestrictions.map(() => []),
                showContentOrRestriction: 'Restriction'
            });
            return false;

        }
        const validPendingLicenseRestrictions = [...listOfPendingLicenseRestrictions];
        let validPending = true;
        if (validPendingLicenseRestrictions.length > 0) {

            state.selectedPendingLicenseRestrictions.forEach((selectedLicenseRestricted, index) => {

                if (selectedLicenseRestricted.length === 0) {

                    validPending = false;
                    validPendingLicenseRestrictions[index].Valid = false;

                } else {

                    validPendingLicenseRestrictions[index].Valid = true;

                }

            });

        }
        if (!validPending) {

            setState({
                pendingLicenseRestrictions: validPendingLicenseRestrictions,
                showContentOrRestriction: 'Restriction'
            });
            return false;

        }

        const orderIdsToAdd = state.selectedPendingLicenseRestrictions
            .flat()
            .map(licenseRestriction => licenseRestriction.OrderId);

        insertPendedLicenseRestrictionDeliverables(
            orderIdsToAdd,
            validPendingLicenseRestrictions,
            buckets,
            selectedCheckBoxes
        );

        orderIds = [...new Set([...orderIds, ...orderIdsToAdd])];
        listOfPendingLicenseRestrictions = [];
        const finalContentData = {
            content: selectedCheckBoxes,
            transactionDetails: {
                ...DEFAULT_TRANSACTION_FORM,
                NumOfBooks: counts.bookCount,
                NumOfCourses: counts.courseCount,
                NumOfTests: 100,
                NumXEDAccess: counts.xedCourseAdded,
                OrderIds: [...new Set(orderIds)],
                PendingBooks: pendingBooks,
                PendingCourses: pendingCourses,
                PendingTests: pendingTests
            }
        };
        return { canCloseModal: selectedCheckBoxes.length > 0, contentData: finalContentData } as any;

    };

    const handleSaveClick = async () => {

        if (state.selected.length > 0) {

            const result = await savedOrderIdsAndDeliverableIds();
            if (result && result.canCloseModal && onSuccess) {

                onSuccess(result.contentData);

            }

        } else {

            createToast(formatMessage({ id: 'you_need_to_select_content_in_order_to_c.8ded2214', defaultMessage: 'You need to select content in order to continue' }), 'info');

        }

    };

    return (
        <>
            <ContentWrapper style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <ContentPickerView tree={tree}/>
            </ContentWrapper>
            <ActionsWrapper>
                {onClose && (
                    <GMetrixButton className="mx-2" onClick={state.showContentOrRestriction !== 'Content' ? () => tree.setShowContentOrRestriction('Content') : onClose} variant="secondary">
                        {state.showContentOrRestriction !== 'Content' ? <FormattedMessage defaultMessage="Back" id="back.b6238c9e" /> : <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />}
                    </GMetrixButton>
                )}
                <GMetrixButton disabled={state.selected?.length === 0} onClick={handleSaveClick} variant="primary" >
                    <FormattedMessage defaultMessage="Next" id="next.50b7834e" />
                </GMetrixButton>
            </ActionsWrapper>
        </>
    );

};

/**
 * Context-aware dialog variant: sources selected content from the Tabs page context, persists
 * additions/removals through the TransactionApi, updates the shared context, and closes itself.
 */
const DialogVariant = (props: FindContentDialogProps) => {

    const { currentContext, onCancel, onSuccess, show, showCancelButton = true } = props;

    const tree = useContentTree();
    const { setState, state } = tree;
    const { formatMessage } = useIntl();
    const { createToast, onErrorToast } = useNotification();
    const { isLoaded, pageContext, update } = useContext(currentContext);
    const { isSystemMode } = useApp();

    // When a SysAdmin is not managing a specific partner (system mode), scope the available
    // content to the partner that owns the classroom/access code — sourced from the partner
    // object returned by the getAllPaged call — so they only see content that classroom or
    // access code has access to. Returns null otherwise so the backend uses the session partner.
    const getContentPartnerId = (): number | null => {

        if (!isSystemMode) {

            return null;

        }
        const data = pageContext?.Data;
        const groupPartnerId = (data as GroupData)?.groupDetails?.Partner?.Id;
        const transactionPartnerId = data?.transactionDetails?.Partner?.Id;
        return groupPartnerId ?? transactionPartnerId ?? null;

    };

    const setLicensesIfTheyArentThere = async () => {

        if (pageContext && !isLoaded('licenses')) {

            const licenseObj = await TransactionApi.GetAllLicenses(getContentPartnerId()).catch(console.error);
            if (licenseObj && licenseObj.licenses.length > 0) {

                update({
                    licenses: licenseObj.licenses
                });

            }

        }

    };

    useEffect(() => {

        setLicensesIfTheyArentThere();

    }, [pageContext?.Id]);

    const setAlreadySelectedCheckBoxes = (contentFlatList: ContentDeliverable[]) => {

        if (pageContext?.Data.content && pageContext.Data.content.length > 0 && contentFlatList.length > 0) {

            setState({ check: { ids: buildCheckedIds(pageContext.Data.content, contentFlatList, false) } });

        }

    };

    const renderInitiallySelected = () => {

        if (pageContext?.Data.content) {

            setState({ selected: pageContext.Data.content });

        }

    };

    useEffect(() => {

        state.showContentOrRestriction === 'Restriction' && setState({ loadingAllContent: true });
        if (!show) {

            setState({
                certificateList: state.certificateList.map(certificate => {

                    certificate.CertificateChecked = false;
                    return certificate;

                }),
                check: { ids: [] },
                deliverableFilterList: state.deliverableFilterList.map(deliverable => {

                    deliverable.Checked = false;
                    return deliverable;

                }),
                expand: { ids: [] },
                expandSelectedContent: false,
                loadingAllContent: true,
                pendingLicenseRestrictions: [],
                searchAllTerm: '',
                showContentOrRestriction: 'Content',
                vendorList: state.vendorList.map(vendor => {

                    vendor.Checked = false;
                    return vendor;

                })
            });
            if (state.check.ids && state.check.ids.length === 0) {

                setAlreadySelectedCheckBoxes(state.flatListAllContent);

            }

        } else {

            if (state.allContent.length > 0) {

                setTimeout(() => {

                    setState({ loadingAllContent: false });

                }, 500);
                setAlreadySelectedCheckBoxes(state.flatListAllContent);

            } else {

                TransactionApi.GetAllContentForTransactions(false, false, getContentPartnerId())
                    .then(response => {

                        setState({
                            allContent: response.contentTreeView as CategoryTree[],
                            flatListAllContent: response.contentFlatList
                        });
                        setAlreadySelectedCheckBoxes(response.contentFlatList as ContentDeliverable[]);
                        tree.getAndSetCertAndVendorLists(response.contentFlatList);

                    })
                    .catch(console.error)
                    .finally(() => setState({ loadingAllContent: false }));
                TransactionApi.GetAllLicenses(getContentPartnerId())
                    .then(res => setState({ licenseRestrictions: res.licenses }))
                    .catch(console.error);

            }

        }

    }, [show]);

    useEffect(() => {

        renderInitiallySelected();

    }, [pageContext]);

    useEffect(() => {

        if (pageContext?.Data.transactionDetails?.IsDuplicated && state.check.ids && state.check.ids.length > 0 && state.runOnlyOnce) {

            expandCollapseSelectedContent(false);
            setState({ expandSelectedContent: !state.expandSelectedContent, runOnlyOnce: false });

        }

    }, [pageContext?.Data.transactionDetails, state.check]);

    const handlePendingLicenseRestrictions = (
        selectedItem: SelectedContent,
        listOfPendingLicenseRestrictions: PendingLicenseRestriction[]
    ) => {

        const nonAvailableLicenseList: NonAvailableLicense[] = [];

        if (pageContext?.Data.licenses && pageContext.Data.mostRestrictiveLicense) {

            selectedItem.OrderIds = removeOrdersWithLessUsesThanCurrentTransactionStudents(
                selectedItem,
                pageContext.Data.licenses,
                pageContext.Data.mostRestrictiveLicense,
                nonAvailableLicenseList,
                pageContext.Data.transactionDetails?.OrderIds ?? []
            );

        }

        addPendingLicenseRestriction(selectedItem, listOfPendingLicenseRestrictions, state.licenseRestrictions, nonAvailableLicenseList);

    };

    const savedOrderIdsAndDeliverableIds = async () => {

        if (pageContext) {

            const pendingTests: SelectedContentReference[] = [];
            const pendingBooks: SelectedContentReference[] = [];
            const pendingCourses: SelectedContentReference[] = [];
            let orderIds: number[] = [...new Set(pageContext.Data.transactionDetails?.OrderIds)];
            let listOfPendingLicenseRestrictions: PendingLicenseRestriction[] = [];
            const selectedCheckBoxes = [...state.selected];
            const buckets: PendingBuckets = { orderIds, pendingBooks, pendingCourses, pendingTests };
            const counts: PendingCounts = {
                bookCount: pageContext.Data.transactionDetails?.EBookIds?.length ?? 0,
                courseCount: pageContext.Data.transactionDetails?.CourseIds?.length ?? 0,
                xedCourseAdded: pageContext.Data.transactionDetails?.NumXEDAccess ?? 0
            };
            const skippedContent = [];
            for (let i = 0; i < selectedCheckBoxes.length; i++) {

                const selectedItem = { ...selectedCheckBoxes[i] };
                if (!selectedItem.OrderIds) {

                    // Already-added content can lack orderIds; only pending adds are a real failure.
                    if (selectedItem.PendingAdd) {

                        skippedContent.push(selectedItem.Name);

                    }
                    continue;

                }
                const selectedItemReference: SelectedContent = {
                    DeliverableTypeId: selectedItem.DeliverableTypeId,
                    Id: selectedItem.Id,
                    Name: selectedItem.Name,
                    OrderIds: filterOutNonExistentOrderIds(selectedItem.OrderIds, state.licenseRestrictions)
                };

                if (selectedItemReference.OrderIds.length === 0 && selectedItem.PendingAdd) {

                    console.error(new Error(`The orderId(s) of this piece of content aren't active. ${selectedItem.OrderIds.toString()}`));
                    onErrorToast(formatMessage({ id: 'name_does_not_have_an_active_order_id.ab839133', defaultMessage: '{name} does not have an active order id.' }, { name: selectedItemReference.Name }));

                }

                classifyPendingDeliverable(
                    selectedItem,
                    selectedItemReference,
                    buckets,
                    counts,
                    listOfPendingLicenseRestrictions,
                    handlePendingLicenseRestrictions,
                    false
                );

            }
            if (skippedContent.length > 0) {

                createToast(formatMessage({ id: 'some_content_was_not_able_to_be_added_co.d32f9490', defaultMessage: 'Some content was not able to be added: {content}' }, { content: skippedContent.toString() }), 'danger');

            }
            if (listOfPendingLicenseRestrictions.length !== state.pendingLicenseRestrictions.length) {

                setState({
                    pendingLicenseRestrictions: listOfPendingLicenseRestrictions,
                    selectedPendingLicenseRestrictions: listOfPendingLicenseRestrictions.map(() => [])
                });
                tree.setShowContentOrRestriction('Restriction');
                return false;

            }
            const validPendingLicenseRestrictions = [...listOfPendingLicenseRestrictions];
            let validPending = true;
            if (validPendingLicenseRestrictions.length > 0) {

                for (let i = 0; i < state.selectedPendingLicenseRestrictions.length; i++) {

                    if (state.selectedPendingLicenseRestrictions[i].length === 0) {

                        validPending = false;
                        validPendingLicenseRestrictions[i].Valid = false;

                    } else {

                        validPendingLicenseRestrictions[i].Valid = true;

                    }

                }

            }
            if (!validPending) {

                setState({ pendingLicenseRestrictions: validPendingLicenseRestrictions });
                tree.setShowContentOrRestriction('Restriction');
                return false;

            }
            const pendingDelete: ContentDeliverable[] = [];
            const contentThatIsntPending = selectedCheckBoxes
                ? selectedCheckBoxes.filter(pieceOfContent => !pieceOfContent.PendingAdd)
                : [];
            if (
                selectedCheckBoxes &&
                contentThatIsntPending.length > 0 &&
                state.check.ids
            ) {

                selectedCheckBoxes.forEach(pieceOfContent => {

                    let exists = false;
                    state.check?.ids?.forEach(key => {

                        const splitKey = key.split('_');
                        const deliverableId = parseInt(splitKey[splitKey.length - 2]);
                        const contentId = parseInt(splitKey[splitKey.length - 1]);
                        if (pieceOfContent.Id === contentId && pieceOfContent.DeliverableTypeId === deliverableId) {

                            exists = true;

                        }

                    });
                    if (!exists) {

                        pendingDelete.push(pieceOfContent);

                    }

                });
                if (pendingDelete.length > 0 && pageContext?.Data.transactionDetails) {

                    const testIds: number[] = [];
                    const courseIds: number[] = [];
                    const bookIds: number[] = [];
                    pendingDelete.forEach(pieceOfContent => {

                        switch (true) {

                            case pieceOfContent.DeliverableTypeId === DeliverableType.Test:
                                testIds.push(pieceOfContent.Id);
                                break;
                            case pieceOfContent.DeliverableTypeId === DeliverableType.Course:
                                courseIds.push(pieceOfContent.Id);
                                break;
                            case pieceOfContent.DeliverableTypeId === DeliverableType.Book:
                                bookIds.push(pieceOfContent.Id);
                                break;

                        }

                    });

                    await TransactionApi.DeleteContentFromTransaction(
                        pageContext.Data.transactionDetails.Id,
                        testIds,
                        bookIds,
                        courseIds
                    );

                }

            }

            const orderIdsToAdd = state.selectedPendingLicenseRestrictions
                .flat()
                .map(licenseRestriction => licenseRestriction.OrderId);

            insertPendedLicenseRestrictionDeliverables(
                orderIdsToAdd,
                validPendingLicenseRestrictions,
                buckets
            );
            orderIds = [...new Set([...orderIds, ...orderIdsToAdd])];
            listOfPendingLicenseRestrictions = [];
            let transactionDetails = pageContext.Data.transactionDetails;
            if (pageContext.Data.transactionDetails) {

                let newExpirationDate: Date | undefined = undefined;
                if (pageContext.Data.transactionDetails.ExpirationDate === undefined) {

                    [...new Set(orderIds)].forEach(orderId => {

                        const license = pageContext.Data.licenses?.find(license => license.OrderId === orderId);
                        if (newExpirationDate) {

                            if (license && new Date(license.OrderExpireDate) < new Date(newExpirationDate)) {

                                newExpirationDate = new Date(license.OrderExpireDate);

                            }

                        } else {

                            if (license) {

                                newExpirationDate = new Date(license.OrderExpireDate);

                            }

                        }

                    });

                }

            } else {

                const result = await TransactionApi.Create({
                    ...DEFAULT_TRANSACTION_FORM,
                    ExpirationDate: pageContext.Data.mostRestrictiveLicense?.license.OrderExpireDate ?? new Date(),
                    Name: pageContext.Title,
                    OrderIds: orderIds,
                    TransactionType: TransactionType.Classroom
                });
                if (result.Done && result.Id) {

                    transactionDetails = {
                        ...DEFAULT_TRANSACTION_FORM,
                        ExpirationDate: pageContext.Data.mostRestrictiveLicense?.license.OrderExpireDate ?? new Date(),
                        Id: result.Id,
                        Name: pageContext.Title,
                        OrderIds: orderIds,
                        TransactionType: TransactionType.Classroom
                    };
                    await TransactionApi.AddGroupToTransaction([result.Id], [pageContext.Id]);

                }

            }
            if (transactionDetails) {

                const newTransactionDetails = {
                    ...transactionDetails,
                    NumOfBooks: counts.bookCount,
                    NumOfCourses: counts.courseCount,
                    NumOfTests:
                        pendingTests.length > 0 &&
                            (transactionDetails.NumOfTests === 0 ||
                                transactionDetails.NumOfTests === undefined)
                            ? 100
                            : transactionDetails.NumOfTests,
                    NumXEDAccess: counts.xedCourseAdded,
                    OrderIds: [...new Set(orderIds)],
                    PendingBooks: pendingBooks,
                    PendingCourses: pendingCourses,
                    PendingTests: pendingTests
                };

                const response = await TransactionApi.Update(newTransactionDetails).catch(console.error);
                if (response && !response.Done) {

                    console.error(new Error(response.Message));
                    return false;

                }

                await addContentToTransactionChunked(
                    transactionDetails.Id,
                    pendingTests,
                    pendingBooks,
                    pendingCourses
                ).catch(console.error);

                update({
                    content: pendingDelete.length > 0
                        ? selectedCheckBoxes.filter(pieceOfContent => !pendingDelete.some(
                            deleted => deleted.Id === pieceOfContent.Id &&
                                deleted.DeliverableTypeId === pieceOfContent.DeliverableTypeId
                        ))
                        : selectedCheckBoxes,
                    transactionDetails: newTransactionDetails
                });
                if (skippedContent.length === 0) {

                    createToast(formatMessage({ id: 'the_content_was_saved_successfully.6c899203', defaultMessage: 'The content was saved successfully' }), 'success');

                }

            }
            return true;

        }
        return false;

    };

    const handleCloseModal = () => {

        if (onSuccess) {

            tree.setShowContentOrRestriction('Content');
            onSuccess();

        }

    };

    const handleSaveClick = async () => {

        try {

            setState({ loadingSaveButton: true });
            if (pageContext && onSuccess) {

                const canCloseModal = await savedOrderIdsAndDeliverableIds();

                if (canCloseModal) {

                    handleCloseModal();

                }

            }
            setState({ loadingSaveButton: false });

        } catch (error) {

            console.error(error);
            setState({ loadingSaveButton: false });

        }

    };

    const expandCollapseSelectedContent = (expandValue: boolean) => {

        let listOfExpandKeys: string[] = [...state.expand.ids];
        if (state.check.ids) {

            for (let i = 0; i < state.check.ids.length; i++) {

                const key = state.check.ids[i];
                const splitKey = key.split('_');
                const firstKey = splitKey[0];
                const secondKey = [splitKey[0], splitKey[1]].join('_');
                const thirdKey = [splitKey[0], splitKey[1], splitKey[2]].join('_');
                const firstKeyExists = listOfExpandKeys.find(expandKey => expandKey === firstKey);
                const secondKeyExists = listOfExpandKeys.find(expandKey => expandKey === secondKey);
                const thirdKeyExists = listOfExpandKeys.find(expandKey => expandKey === thirdKey);

                if (expandValue) {

                    if (firstKeyExists) listOfExpandKeys = listOfExpandKeys.filter(expandKey => expandKey !== firstKey);
                    if (secondKeyExists) listOfExpandKeys = listOfExpandKeys.filter(expandKey => expandKey !== secondKey);
                    if (thirdKeyExists) listOfExpandKeys = listOfExpandKeys.filter(expandKey => expandKey !== thirdKey);

                } else {

                    if (!firstKeyExists) listOfExpandKeys.push(firstKey);
                    if (!secondKeyExists) listOfExpandKeys.push(secondKey.split('|')[0]);
                    if (!thirdKeyExists) listOfExpandKeys.push(thirdKey);

                }

            }
            setState({ expand: { ids: listOfExpandKeys } });

        }

    };

    return (
        <GMetrixDialog
            dialogLabel={(
                <>
                    {state.showContentOrRestriction === 'Content' ? (
                        <div className="d-flex justify-content-between" style={{ maxWidth: '100%' }}>
                            <GMetrixButton
                                onClick={() => {

                                    expandCollapseSelectedContent(state.expandSelectedContent);
                                    setState({ expandSelectedContent: !state.expandSelectedContent });

                                }}
                                disabled={state.check.ids && state.check.ids.length === 0}
                                variant="primary"
                            >
                                {!state.expandSelectedContent
                                    ? <FormattedMessage defaultMessage="Expand Selected Items" id="expand_selected_items.ffcfb47c" />
                                    : <FormattedMessage defaultMessage="Collapse Selected Items" id="collapse_selected_items.1ed6a02b" />}
                            </GMetrixButton>
                        </div>
                    ) : (
                        <div className="find-content-dialog__restriction-tab">
                            <div
                                className="find-content-dialog__go-back"
                                onClick={() => tree.setShowContentOrRestriction('Content')}
                                onKeyDown={e => e.key === 'Enter' && tree.setShowContentOrRestriction('Content')}
                                tabIndex={0}
                            >
                                <i className="fa-solid fa-chevron-left"></i>
                                {' '}
                                <FormattedMessage defaultMessage="Go Back" id="go_back.0bc6208f" />
                            </div>
                            <div>
                                <FormattedMessage defaultMessage="Restrictions" id="restrictions.16d3abf7" />
                            </div>
                        </div>
                    )}
                </>
            )}
            onCancel={() => {

                if (state.showContentOrRestriction === 'Restriction') {

                    tree.setShowContentOrRestriction('Content');
                    return;

                }
                onCancel();
                setState({ check: { ids: [] } });

            }}
            actionButtonClick={handleSaveClick}
            actionButtonDisabled={state.loadingSaveButton}
            actionButtonText={state.loadingSaveButton ? <i className="fa-solid fa-spinner-third fa-spin" ></i> : formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
            closeButtonText={state.showContentOrRestriction === 'Restriction' ? formatMessage({ id: 'back.b6238c9e', defaultMessage: 'Back' }) : undefined}
            manualHeight="95vh"
            show={show}
            showCancelButton={showCancelButton}
            size="xl"
        >
            <ContentPickerView tree={tree}/>
        </GMetrixDialog>
    );

};

/**
 * Unified content finder. `variant="picker"` renders the standalone Modal-hosted picker that
 * hands its payload back through onSuccess; `variant="dialog"` renders the context-aware
 * GMetrixDialog that persists the selection itself.
 */
export const FindContent = (props: FindContentProps) => (props.variant === 'dialog' ? <DialogVariant {...props}/> : <PickerVariant {...props}/>);
